import { useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/i18n/LanguageContext';
import { ARTIFACT_CONFIG, PANEL_SUPPORTED_TYPES, type ArtifactReference } from './artifact-config';

export type { ArtifactReference };

interface ArtifactCardProps {
  artifact: ArtifactReference;
  projectId?: string;
  onOpen?: (artifact: ArtifactReference) => void;
}

export default function ArtifactCard({ artifact, projectId: _projectId, onOpen }: ArtifactCardProps) {
  const { t } = useTranslation('chat');
  const navigate = useNavigate();
  const { localizedPath } = useLanguage();
  const config = ARTIFACT_CONFIG[artifact.type];

  if (!config) return null;

  const Icon = config.icon;
  const isCompleted = artifact.status === 'completed';
  const basePath = artifact.recordId
    ? `${config.path}?recordId=${artifact.recordId}`
    : config.path;
  const targetUrl = localizedPath(basePath);

  // Артефакты с текстом открываем в боковой панели, остальные — переходом на страницу
  const canOpenInPanel = Boolean(onOpen && artifact.recordId && PANEL_SUPPORTED_TYPES.has(artifact.type));

  const handleOpen = () => {
    if (canOpenInPanel) {
      onOpen!(artifact);
    } else {
      navigate(targetUrl);
    }
  };

  return (
    <div
      className="flex items-center gap-3 mt-2 p-3 rounded-[12px] border border-[var(--grey-accent,#eeeeee)] bg-white cursor-pointer hover:border-[var(--main-brand,#2197f1)] transition-colors"
      onClick={handleOpen}
    >
      <div className={`flex-shrink-0 ${config.color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-manrope font-semibold text-[13px] leading-[18px] text-[color:var(--black-text,#0d0d0d)]">
            {t(config.labelKey)}
          </span>
          {isCompleted ? (
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
          ) : (
            <XCircle className="h-3.5 w-3.5 text-red-400 flex-shrink-0" />
          )}
        </div>
        {artifact.summary && (
          <p className="font-segoe text-[12px] leading-[16px] text-[color:var(--grey-text,#565858)] line-clamp-2">
            {artifact.summary}
          </p>
        )}
      </div>
      <button
        className="flex-shrink-0 px-[12px] py-[4px] rounded-[8px] bg-[var(--main-brand,#2197f1)] text-white font-manrope font-semibold text-[12px] leading-[16px] hover:opacity-90 transition-opacity"
        onClick={(e) => {
          e.stopPropagation();
          handleOpen();
        }}
      >
        {t('artifact.buttonOpen')}
      </button>
    </div>
  );
}
